import {
  NUMERICAL_GRADE_BANDS,
  GradeBandKey,
} from "@/constants/GradeAndLexileLevels";

/**
 * Find the exact or next best matching grade band.
 * @param {string} currentGradeBand // ex: 'GRADE_12'
 * @param {array} possibleGradeBands // ex: ['GRADE_11', 'GRADE_9', 'GRADE_HIGH_SCHOOL']
 * @returns {string|null} // ex: 'GRADE_11'
 */
const findClosestGradeBand = (
  currentGradeBand: GradeBandKey,
  possibleGradeBands: GradeBandKey[],
): string | null => {
  try {
    if (isNaN(possibleGradeBands?.length) || possibleGradeBands.length === 0) {
      throw new Error(
        "Missing Grade Band Data: Unable to determine the closest grade band to the current grade band because no possible grade bands were provided",
      );
    }
  } catch (e) {
    console.error(e);
    return currentGradeBand;
  }

  /**
   * In some cases, there may be two levels with the same absolute value from
   * the target value, e.g grade_5 being equidistant from grade_3 and grade_7.
   * We order the bands in ascending order so that the lower grade
   * in the array is first. We'll favor this lower value when returning a match.
   */

  const sortedGradeBands = possibleGradeBands.sort(
    (a, b) => NUMERICAL_GRADE_BANDS[a] - NUMERICAL_GRADE_BANDS[b],
  );

  const closest = sortedGradeBands.reduce((prev, curr) => {
    return Math.abs(
      NUMERICAL_GRADE_BANDS[curr] - NUMERICAL_GRADE_BANDS[currentGradeBand],
    ) <
      Math.abs(
        NUMERICAL_GRADE_BANDS[prev] - NUMERICAL_GRADE_BANDS[currentGradeBand],
      ) ||
      NUMERICAL_GRADE_BANDS[curr] === NUMERICAL_GRADE_BANDS[currentGradeBand]
      ? curr
      : prev;
  });
  return closest;
};

export default findClosestGradeBand;
